<template>
  <Form @submit="onBeforeSubmit" :validation-schema="validationSchema" ref="form" :initial-values="formValues">
    <div class="uk-modal-header form-header">
      <div class="uk-text-bold uk-text-large">
        {{ title }}
      </div>
    </div>
    <div class="uk-modal-body">
      <div class="modal-container">
        <FormError :errors="globalErrors" />
        <div class="field">
          <Field name="username" v-slot="{ field }">
            <FieldLabel title="ユーザーID" :required="true" />
            <span>&ensp;(重複するIDは登録できません)</span>
            <input
              class="uk-input"
              type="text"
              v-bind="field"
              placeholder="example@hyoumanbox.com"
              :readonly="employee && employee.id"
            />
          </Field>
          <FieldError name="username" />
        </div>
        <div class="field">
          <Field name="email" v-slot="{ field }">
            <HelpIcon tooltipText="メールアドレスの間違いにご注意ください" position="top-left" cls="uk-active uk-width-auto" />
            <FieldLabel title="メールアドレス" :required="true" />
            <input class="uk-input" type="text" v-bind="field" :readonly="!isEditable" />
          </Field>
          <FieldError name="email" />
        </div>
        <div class="field column-2">
          <div>
            <Field name="lastName" v-slot="{ field }">
              <FieldLabel title="姓" :required="true" />
              <input class="uk-input" type="text" v-bind="field" :readonly="!isEditable" />
            </Field>
            <FieldError name="lastName" />
          </div>
          <div>
            <Field name="firstName" v-slot="{ field }">
              <FieldLabel title="名" :required="true" />
              <input class="uk-input" type="text" v-bind="field" :readonly="!isEditable" />
            </Field>
            <FieldError name="firstName" />
          </div>
        </div>
        <div class="field gender">
          <FieldLabel title="性別" />
          <div class="field-item">
            <Field v-slot="{ field }" name="gender" type="radio" value="Male">
              <label>
                <input type="radio" name="gender" v-bind="field" value="Male" :disabled="!isEditable" />
                男性
              </label>
            </Field>
            <Field v-slot="{ field }" name="gender" type="radio" value="Female">
              <label>
                <input type="radio" name="gender" v-bind="field" value="Female" :disabled="!isEditable" />
                女性
              </label>
            </Field>
            <Field v-slot="{ field }" name="gender" type="radio" value="Unknown">
              <label>
                <input type="radio" name="gender" v-bind="field" value="Unknown" :disabled="!isEditable" />
                未回答
              </label>
            </Field>
          </div>
        </div>
        <div class="field column-2">
          <div>
            <FieldLabel title="雇用形態" :required="true" />
            <Field name="contractType" v-slot="{ field }" v-model="contractEmployee">
              <select class="uk-select" v-bind="field">
                <option v-for="contract in contractOptions" :key="contract.code" :value="contract.code" :disabled="!isEditable">
                  {{ contract.name }}
                </option>
              </select>
            </Field>
            <FieldError name="contractType" />
          </div>
          <div class="fade-in" v-show="contractEmployee === 'employee'">
            <FieldLabel title="採用区分" />
            <div class="field-item">
              <Field v-slot="{ field }" name="career" type="radio" value="new">
                <label>
                  <input type="radio" name="career" v-bind="field" value="new" :disabled="!isEditable" />
                  新卒
                </label>
              </Field>
              <Field v-slot="{ field }" name="career" type="radio" value="mid">
                <label>
                  <input type="radio" name="career" v-bind="field" value="mid" :disabled="!isEditable" />
                  中途
                </label>
              </Field>
            </div>
            <FieldError name="career" />
          </div>
        </div>
        <div class="field column-2">
          <div class="enrollment-container">
            <HelpIcon
              tooltipText="退職を選択すると課金対象およびダッシュボードのサマリ計算から除外されます<br />また、分析画面で退職者リストに表示が移動します"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <FieldLabel title="在籍状況" :required="true" />
            <Field name="enrollment" v-slot="{ field }" v-model="enrollmentRetirement">
              <select class="uk-select" v-bind="field">
                <option v-for="enrollment in enrollments" :key="enrollment.id" :value="enrollment.id" :disabled="!isEditable">
                  {{ enrollment.name }}
                </option>
              </select>
            </Field>
            <FieldError name="enrollment" />
          </div>
          <div
            v-show="['retiring', 'retired'].includes(enrollmentRetirement)"
            class="fade-in"
            style="position: relative; z-index: 1;"
          >
            <HelpIcon
              tooltipText="退職日が未来の場合、退職日の翌日に在籍状況を退職に自動更新します"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <FieldLabel title="退職日" :required="true" />
            <Field name="retirementDate">
              <Datepicker
                width="180"
                class="uk-input"
                v-model="formValues.retirementDate"
                :locale="locale"
                input-format="yyyy-MM-dd"
                :weekStartsOn="0"
                placeholder=""
                :clearable="true"
                :disabled="!isEditable"
              />
            </Field>
            <FieldError name="retirementDate" />
          </div>
        </div>
        <div class="field">
          <div class="fade-in" v-show="['retiring', 'retired'].includes(enrollmentRetirement)">
            <HelpIcon
              tooltipText="「在籍状況」が「退職」の場合は理由を選択してください"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <FieldLabel title="退職理由" :required="true" />
            <Field name="retirementReasons">
              <Multiselect
                class="multiselect-blue"
                v-model="formValues.retirementReasons"
                v-bind="retirementReasonOptions"
                :disabled="!isEditable"
              />
            </Field>
            <FieldError name="retirementReasons" />
          </div>
        </div>
      </div>
      <div class="half-line"></div>
      <div class="modal-container">
        <div class="field">
          <FieldLabel title="部署" :required="true" />
          <Field name="departmentId" v-slot="{ field }">
            <select class="uk-select" v-bind="field">
              <option v-for="department in departments" :key="department.id" :value="department.id" :disabled="!isEditable">
                {{ department.name }}
              </option>
            </select>
          </Field>
          <FieldError name="departmentId" />
          <div v-if="!addAdditionalDepartments">
            <span class="span-link" @click="addAdditionalDepartments = !addAdditionalDepartments">兼務部署を追加（3つまで）</span>
          </div>
          <div v-if="!addAnotherDepartments">
            <span class="span-link" @click="addAnotherDepartments = !addAnotherDepartments">関連部署を追加（３つまで）</span>
          </div>
        </div>
        <div
          v-show="addAdditionalDepartments"
          class="field"
          :class="{ 'fade-in': addAdditionalDepartments }"
          style="position: relative; z-index: 1;"
        >
          <HelpIcon
            tooltipText="兼務部署を3部署まで設定できます。ダッシュボードのサマリ集計に含まれます。"
            position="top-left"
            cls="uk-active uk-width-auto"
          />
          <FieldLabel title="兼務部署" />
          <Field name="aditionalDepartmentIds">
            <Multiselect
              class="multiselect-blue"
              v-model="formValues.aditionalDepartmentIds"
              v-bind="departmentOptions"
              :options="departments"
              :disabled="!isEditable"
            />
          </Field>
          <FieldError name="aditionalDepartmentIds" />
        </div>
        <div v-show="addAnotherDepartments" class="field" :class="{ 'fade-in': addAnotherDepartments }">
          <HelpIcon
            tooltipText="兼務部署以外に参照したい部署を3部署まで設定できます。ダッシュボードのサマリ集計に含まれません。"
            position="top-left"
            cls="uk-active uk-width-auto"
          />
          <FieldLabel title="関連部署" />
          <Field name="anotherDepartmentIds">
            <Multiselect
              class="multiselect-blue"
              v-model="formValues.anotherDepartmentIds"
              v-bind="departmentOptions"
              :options="departments"
              :disabled="!isEditable"
            />
          </Field>
          <FieldError name="anotherDepartmentIds" />
        </div>
        <div class="field column-2">
          <div>
            <FieldLabel title="職種" :required="true" />
            <Field name="occupationId" v-slot="{ field }">
              <select class="uk-select" v-bind="field">
                <option
                  v-for="occupation in storeInstance.getOccupations()"
                  :key="occupation.id"
                  :value="occupation.id"
                  :disabled="!isEditable"
                >
                  {{ occupation.name }}
                </option>
              </select>
            </Field>
            <FieldError name="occupationId" />
          </div>
          <div>
            <FieldLabel title="役職" :required="false" />
            <Field name="positionId" v-slot="{ field }">
              <select class="uk-select" v-bind="field">
                <option value="" :disabled="!isEditable"></option>
                <option
                  v-for="position in storeInstance.getPositions()"
                  :key="position.id"
                  :value="position.id"
                  :disabled="!isEditable"
                >
                  {{ position.name }}
                </option>
              </select>
            </Field>
            <FieldError name="positionId" />
          </div>
        </div>
        <div class="field">
          <div>
            <HelpIcon
              tooltipText="クライアント管理者権限はサーベイ発行や各種設定変更に必要な権限です<br />クライアント内で1人以上必要です"
              position="top-left"
              cls="uk-active uk-width-auto"
            />
            <FieldLabel title="権限" :required="true" />
            <Field name="role" v-slot="{ field }">
              <select class="uk-select" v-bind="field">
                <option v-for="role in roles" :key="role.value" :value="role.value" :disabled="!isEditable">
                  {{ role.name }}
                </option>
              </select>
            </Field>
            <FieldError name="role" />
          </div>
        </div>
        <template v-if="employee && employee.id">
          <div class="field">
            <FieldLabel title="ログインメール再送信" />
            <Field name="resendInviteMail">
              <ActionButton
                title="再送信"
                color="default"
                @click="onResendMailButtonClick($event)"
                :isLoading="isSending"
                :disabled="!isEditable"
              />
              <span v-if="sendComplete" style="font-size: 0.8em; margin-left: 10px;" v-html="sendMassage"></span>
            </Field>
            <FieldError name="resendInviteMail" />
          </div>
        </template>
        <template v-else>
          <input class="uk-checkbox" type="checkbox" id="noSendInviteMailFlg" v-model="noSendInviteMailFlg" />
          <label for="noSendInviteMailFlg">招待メールを送信しない</label>
        </template>
      </div>
    </div>
    <div>
      <div class="uk-modal-footer uk-text-right form-action">
        <div>
          <ActionButton title="戻る" class="uk-modal-close" toggleTarget=".employee-form-modal" />
        </div>
        <div class="form-action-main">
          <ActionButton v-if="isEditable" title="確定" color="primary" type="submit" :disabled="processing" />
          <ActionButton v-if="isEditable && employee && employee.id" title="削除" @click="onDeleteButtonClick" color="warn" />
        </div>
      </div>
    </div>
  </Form>
</template>
<style lang="scss" scoped>
@include modal-form;
.uk-modal-body {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
}
.field > button {
  margin-left: 40px;
}
.field-item {
  margin-top: $spacing-small;
  margin-bottom: $spacing-small;
}
input[type="checkbox"] {
  margin-right: $spacing-ex-small;
}
.field.avatar {
  display: flex;
  align-items: center;
  label {
    margin-right: $spacing-small;
  }
}
.form-header {
  display: flex;
  justify-content: space-between;
}
div.modal-container {
  width: 45%;
}
.half-line {
  margin: 0 1rem;
  width: 1px;
  background-color: #ddd;
}
.span-link {
  cursor: pointer;
  text-decoration: underline;
  color: #1a0dab;
  font-size: 0.75em;
}
div.v3dp__datepicker {
  &:deep(div.v3dp__clearable) {
    position: absolute;
    right: 9px;
    top: 10px;
    left: unset;
    cursor: pointer;
  }
}
.fade-in {
  animation-name: fadeIn;
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  opacity: 0;
}
@keyframes fadeIn {
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import FormMixin from "../../helpers/mixins/form";
import { Employee, SystemApi, EmployeeApi, ContractTypeEnum, EnrollmentEnum, Department } from "../../autogen";
import { Field, Form, FormActions } from "vee-validate";
import FieldLabel from "../atoms/FieldLabel.vue";
import * as yup from "yup";
import FieldError from "../atoms/FieldError.vue";
import HelpIcon from "../atoms/HelpIcon.vue";
import FormError from "../atoms/FormError.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import Avatar from "../atoms/Avatar.vue";
import Multiselect from "@vueform/multiselect";
import moment from "moment-timezone";
import Datepicker from "vue3-datepicker";
import { ja } from "date-fns/locale";

type FormValues = { [P in keyof Employee]: Employee[P] };
@Options({
  components: {
    Field,
    Form,
    FieldLabel,
    FieldError,
    ActionButton,
    Avatar,
    FormError,
    Multiselect,
    Datepicker,
    HelpIcon
  }
})
export default class EmployeeFormFields extends FormMixin {
  @Prop({ type: Object }) employee?: Employee;
  @Prop({ type: String, required: true }) title!: string;
  @Prop({ type: String, required: true }) formName = "";
  @Prop({ type: Function, required: true }) api!: Function;
  formValues: Partial<FormValues> = {};
  acceptImageType = ["image/png", "image/jpeg"];
  validationSchema!: yup.AnyObjectSchema;
  // DyanmoDB maximum item size is 400KB and base64 format is bigger than original
  isEditable = false;
  enrollments = Object.entries(EnrollmentEnum).map(([id, name]) => ({ id, name }));
  enrollmentRetirement = "";
  retirementReasonOptions = {
    ...this.multiSelectBaseOptions,
    valueProp: "code",
    max: 3,
    options: async () => (await new SystemApi().getRetirementReasons({})).data
  };
  retirementDate: Date | null = null;
  contractOptions = Object.entries(ContractTypeEnum).map(([code, name]) => ({ code, name }));
  contractEmployee = "";
  addAdditionalDepartments = false;
  addAnotherDepartments = false;
  locale = ja;
  departments: Department[] = this.storeInstance.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1];
  departmentOptions = {
    ...this.multiSelectBaseOptions,
    max: 3
  };
  isSending = false;
  sendComplete = false;
  sendMassage = "送信しました";
  noSendInviteMailFlg = false;

  async beforeCreate() {
    this.validationSchema = yup.object({
      username: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "ユーザーID" })),
      firstName: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "名" })),
      lastName: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "姓" })),
      email: yup
        .string()
        .required(this.$t("input_required", { field: "メールアドレス" }))
        .email(this.$t("input_email")),
      contractType: yup.string().required(this.$t("input_required", { field: "雇用形態" })),
      enrollment: yup.string().required(this.$t("input_required", { field: "在籍状況" })),
      retirementDate: yup.string().when(["enrollment"], {
        is: (enrollment: string) => ["retiring", "retired"].includes(enrollment),
        then: yup
          .string()
          .nullable(true)
          .required(this.$t("input_required", { field: "退職日" }))
      }),
      retirementReasons: yup.array().when(["enrollment"], {
        is: (enrollment: string) => ["retiring", "retired"].includes(enrollment),
        then: yup
          .array()
          .nullable(true)
          .min(1, this.$t("input_required", { field: "退職理由" }))
      }),
      departmentId: yup.string().required(this.$t("input_required", { field: "部署" })),
      occupationId: yup.string().required(this.$t("input_required", { field: "職種" })),
      role: yup.string().required(this.$t("input_required", { field: "権限" }))
    });
  }
  async beforeMount() {
    await this.setRoles();
    this.checkEditableField();
    if (this.employee) this.updatedEmployeeField();
    this.stateDepartmentsChange();
  }
  @Watch("employee")
  updatedEmployeeField() {
    this.clearForm();
    this.formValues = this.employee
      ? Object.assign(this.employee, {
          occupationId: this.employee.occupationId,
          positionId: this.employee.positionId,
          retirementDate: new Date(String(this.employee.retirementDate)),
          email: this.employee.email ? this.employee.email : ""
        })
      : {
          username: "",
          lastName: "",
          firstName: "",
          email: "",
          career: "new",
          departmentId: "",
          occupationId: "",
          positionId: "",
          role: "4",
          contractType: "employee",
          enrollment: "enrolled",
          retirementReasons: [],
          retirementDate: ""
        };
    this.checkEditableField();
    this.contractEmployee = (this.formValues?.contractType as string) || "";
    this.enrollmentRetirement = this.employee?.enrollment || "";
    this.addAdditionalDepartments = Boolean(this.formValues.aditionalDepartmentIds);
    this.addAnotherDepartments = Boolean(this.formValues.anotherDepartmentIds);
    this.stateDepartmentsChange();
  }
  clearForm() {
    if (!this.$refs.form) return;
    const formValidation = this.$refs.form as FormActions<yup.AnyObjectSchema>;
    formValidation.resetForm();
    this.globalErrors = [];
  }
  async onBeforeSubmit(formValues: FormValues) {
    await this.onSubmit(async () => {
      const values = Object.assign({}, formValues, {
        career: ["employee"].includes(formValues.contractType as string) ? formValues.career : "",
        retirementDate: ["retiring", "retired"].includes(this.enrollmentRetirement)
          ? moment(this.formValues.retirementDate).format("YYYY-MM-DD")
          : "",
        retirementReasons: ["retiring", "retired"].includes(this.enrollmentRetirement) ? this.formValues.retirementReasons : []
      });
      // when new employee create, choose send invite mail.
      if (!this.employee?.id) Object.assign(values, { noSendInviteMailFlg: this.noSendInviteMailFlg });
      await this.api(values);
      this.$emit("submitted");
    });
  }
  async onDeleteButtonClick() {
    this.onDelete(async () => {
      await new EmployeeApi().deleteEmployee({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        departmentId: this.employee!.departmentId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        employeeId: this.employee!.id
      });
    }, "従業員を削除します。よろしいでしょうか？\n※削除した場合、従業員は完全に削除されます。\n※適性検査のデータも全て表示されなくなります。");
  }
  checkEditableField() {
    this.isEditable = this.$role.isEditable("employee");
  }
  async onResendMailButtonClick(event: Event) {
    if (this.isSending) return;
    this.isSending = true;
    try {
      await new SystemApi().resendInviteMail({ employeeId: this.employee!.id });
    } catch (e) {
      if (e.response.status === 412) {
        this.sendMassage = `時間をおいて再度お試しください`;
      } else {
        this.sendMassage = `不明なエラーが発生しました`;
      }
    } finally {
      this.isSending = false;
    }
    this.sendComplete = true;
    setTimeout(() => {
      this.sendComplete = false;
    }, 2000);
  }
  @Watch("storeInstance.state.departments")
  stateDepartmentsChange() {
    this.departments = this.storeInstance.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1];
  }
}
</script>
